<template>
  <div>
    <v-navigation-drawer
      v-if="isLoggedIn && (isMobile || isOpen)"
      right
      :value="isOpen"
      @input="$emit('update:isOpen', $event)"
      :permanent="!isMobile"
      :temporary="isMobile"
      touchless
      :width="drawerWidth"
      class="right-sidebar-drawer"
      :style="drawerStyles"
    >
      <div class="right-sidebar__content">
        <div class="right-sidebar__header">
          <div class="right-sidebar__header-copy">
            <div class="right-sidebar__title-row">
              <h3 class="right-sidebar__title">{{ sidebarTitle }}</h3>
              <v-chip
                small
                color="#E8F2FF"
                text-color="primary"
                class="right-sidebar__count-chip"
              >
                {{ totalUnitsCount }}
              </v-chip>
            </div>
          </div>

          <div class="right-sidebar__header-actions">
            <v-btn
              v-if="!isMobile"
              icon
              small
              class="header-icon-btn"
              @click="$emit('toggle')"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              v-else
              icon
              small
              class="header-icon-btn"
              @click="$emit('update:isOpen', false)"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="showSearch" class="search-container">
          <v-text-field
            v-model="searchText"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar por unidade, cliente ou matrícula"
            outlined
            dense
            hide-details
            clearable
            class="right-sidebar__search-field"
            @input="debouncedFilterItems"
          />
        </div>

        <div class="items-list-container">
          <div v-if="isLoadingUnits || isLoadingReservoirUnits" class="pa-4">
            <v-skeleton-loader
              v-for="n in 8"
              :key="n"
              type="list-item-avatar-two-line"
              class="mb-3 sidebar-skeleton"
            >
              <template v-slot:avatar>
                <v-skeleton-loader type="avatar" width="40" height="40" />
              </template>
              <template v-slot:title>
                <v-skeleton-loader type="text" width="70%" />
              </template>
              <template v-slot:subtitle>
                <v-skeleton-loader type="text" width="50%" />
              </template>
            </v-skeleton-loader>
          </div>

          <v-list v-else-if="visibleFilteredItems.length" dense class="units-list">
            <div v-for="item in visibleFilteredItems" :key="item.id" class="units-list__group">
              <v-tooltip
                :key="item.id"
                bottom
                :disabled="!shouldShowTooltip(item)"
                :open-delay="300"
                :close-delay="100"
                max-width="280"
                content-class="custom-tooltip"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    class="sidebar-item"
                    :class="{ 'sidebar-item--active': expandedUnitIds.includes(item.id) }"
                  >
                    <v-btn
                      v-if="showSectors && !isDashboard && !isAlerts && !isGoalsForecasts"
                      icon
                      x-small
                      class="sidebar-item__expand"
                      @click.stop="openSectors(item)"
                    >
                      <img
                        :src="chevronDown"
                        alt="chevron down"
                        class="custom-arrow"
                        :class="{ 'flipped': expandedUnitIds.includes(item.id) }"
                      />
                    </v-btn>

                    <div class="sidebar-item__body" @click="handleRowClick(item)">
                      <div class="sidebar-item__avatar">
                        <v-icon v-if="item.icon" :color="item.iconColor || 'primary'">
                          {{ item.icon }}
                        </v-icon>
                        <v-img
                          v-else-if="item.image"
                          :src="item.image"
                          contain
                          class="sidebar-item__image"
                        />
                        <v-icon v-else color="primary">mdi-office-building-outline</v-icon>
                      </div>

                      <div class="sidebar-item__content">
                        <div class="sidebar-item__title-row">
                          <div class="sidebar-item__title">
                            {{ item.title }}
                          </div>
                        </div>
                        <div v-if="item.subtitle" class="sidebar-item__subtitle">
                          {{ item.subtitle }}
                        </div>
                      </div>
                    </div>

                    <div class="sidebar-item__aside">
                      <v-checkbox
                        v-model="selectedUnits"
                        :value="item"
                        color="primary"
                        :ripple="false"
                        dense
                        class="sidebar-item__checkbox"
                      />

                      <v-menu v-if="item.actions" offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop>
                            <v-icon size="18">mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item
                            v-for="action in item.actions"
                            :key="action.label"
                            @click="handleAction(action, item)"
                          >
                            <v-list-item-icon>
                              <v-icon size="16">{{ action.icon }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="text-body-2">
                              {{ action.label }}
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </v-list-item>
                </template>

                <div class="tooltip-content">
                  <div class="tooltip-header">
                    <v-icon size="16" color="primary" class="mr-2">mdi-office-building</v-icon>
                    <span class="tooltip-title">{{ item.title }}</span>
                  </div>

                  <div v-if="item.subtitle" class="tooltip-section">
                    <div class="tooltip-label">
                      <v-icon size="12" color="grey" class="mr-1">mdi-account</v-icon>
                      Cliente
                    </div>
                    <div class="tooltip-value">{{ item.subtitle.split(' - ')[0] }}</div>
                  </div>

                  <div v-if="item.subtitle && item.subtitle.includes(' - ')" class="tooltip-section">
                    <div class="tooltip-label">
                      <v-icon size="12" color="grey" class="mr-1">mdi-card-account-details</v-icon>
                      Matrícula
                    </div>
                    <div class="tooltip-value">{{ item.subtitle.split(' - ')[1] }}</div>
                  </div>

                  <div v-if="item.address" class="tooltip-section">
                    <div class="tooltip-label">
                      <v-icon size="12" color="grey" class="mr-1">mdi-map-marker</v-icon>
                      Endereço
                    </div>
                    <div class="tooltip-value">{{ item.address }}</div>
                  </div>

                  <div v-if="item.badge" class="tooltip-section">
                    <div class="tooltip-label">
                      <v-icon size="12" color="grey" class="mr-1">mdi-target</v-icon>
                      Meta de Economia
                    </div>
                    <div class="tooltip-value">
                      <v-chip :color="item.badgeColor || 'primary'" text-color="white" x-small class="tooltip-chip">
                        {{ item.badge }}
                      </v-chip>
                    </div>
                  </div>

                  <div
                    v-if="item.averageBeforeFoz !== undefined && item.averageBeforeFoz !== null"
                    class="tooltip-section"
                  >
                    <div class="tooltip-label">
                      <v-icon size="12" color="grey" class="mr-1">mdi-chart-line</v-icon>
                      Média antes da Foz
                    </div>
                    <div class="tooltip-value">
                      {{ formatVolume(item.averageBeforeFoz) }}
                    </div>
                  </div>

                  <div
                    v-if="item.economyVolume !== undefined && item.economyVolume !== null"
                    class="tooltip-section"
                  >
                    <div class="tooltip-label">
                      <v-icon size="12" color="grey" class="mr-1">mdi-cash-multiple</v-icon>
                      Meta de economia
                    </div>
                    <div class="tooltip-value">
                      {{ formatVolume(item.economyVolume) }}
                      <span v-if="item.metaEconomia"> ({{ item.metaEconomia }}%)</span>
                    </div>
                  </div>
                </div>
              </v-tooltip>

              <v-list
                v-if="expandedUnitIds.includes(item.id) && !isDashboard && !isAlerts && !isGoalsForecasts"
                dense
                class="sectors-list"
              >
                <div v-for="sector in filteredSectorsByUnit(item.id)" :key="sector.id_doc">
                  <v-list-item class="sector-item">
                    <v-list-item-action>
                      <v-checkbox
                        v-model="selectedSectorIds"
                        :value="sector.id_doc"
                        color="primary"
                        @change="toggleSectorSelection(sector)"
                        :ripple="false"
                        dense
                        :disabled="isSectorDisabled(sector)"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption font-weight-medium">
                        {{ sector.name || `Setor ${sector.id_doc}` }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption sector-item__subtitle">
                        {{ getSectorType(sector) }} •
                        <v-chip x-small :color="getSectorStatusColor(sector)" text-color="white">
                          {{ sector.invoiced ? 'Faturado' : 'Não Faturado' }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </div>
                <v-list-item
                  v-if="!isLoadingSectors[item.id] && (!sectorsByUnitList[item.id] || sectorsByUnitList[item.id].length === 0)"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-caption text-grey-6">Nenhum setor encontrado</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="isLoadingSectors[item.id]">
                  <v-list-item-content>
                    <v-progress-circular indeterminate size="20" color="primary" />
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-list>

          <div v-else class="empty-state">
            Nenhuma unidade encontrada para o termo digitado.
          </div>
        </div>

        <div class="right-sidebar__footer">
          <div class="right-sidebar__footer-meta">
            {{ totalUnitsCount }} unidade(s) carregada(s)
          </div>
          <div
            class="descelect-button"
            :class="{ 'descelect-button--disabled': !hasSelection }"
            @click="handleDeselectAll"
          >
            Limpar seleção
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <v-tooltip v-if="isLoggedIn && showReopenButton" left>
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="sidebar-reopen-btn"
          :class="{
            'sidebar-reopen-btn--active': hasSelection,
            'sidebar-reopen-btn--loading': isReopeningSidebar
          }"
          :style="reopenButtonStyle"
          v-bind="attrs"
          v-on="on"
          @mousedown="handleReopenSidebar"
          @touchstart.passive="handleReopenSidebar"
          @click.prevent="handleReopenSidebar"
          @keydown.enter.prevent="handleReopenSidebar"
          @keydown.space.prevent="handleReopenSidebar"
        >
          <span class="sidebar-reopen-btn__icon-wrap">
            <v-progress-circular
              v-if="isReopeningSidebar"
              indeterminate
              size="22"
              width="2"
              color="primary"
            />
            <v-icon v-else class="sidebar-reopen-btn__icon">
              mdi-format-list-bulleted-square
            </v-icon>
            <span
              v-if="selectedUnitsCount && !isReopeningSidebar"
              class="sidebar-reopen-btn__badge"
            >
              {{ selectedUnitsCount }}
            </span>
            <span
              v-else-if="!isReopeningSidebar"
              class="sidebar-reopen-btn__status-dot"
              :class="{ 'sidebar-reopen-btn__status-dot--active': hasSelection }"
            ></span>
          </span>
          <span class="sidebar-reopen-btn__caption">
            {{ isReopeningSidebar ? 'abrindo' : hasSelection ? 'ativo' : 'abrir' }}
          </span>
        </button>
      </template>
      <span>{{ reopenButtonTooltip }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import UnitsServiceServer from "@/services/server/UnitsService";
import UsersService from "@/services/server/UsersService";
import SectorsService from "@/services/firebase/Sectors/SectorsService";
import DefaultAddImg from "../../../public/img/icons/add_img.png";
import chevronDown from '@/assets/dashboard/chevron-down.png';
import { showErrorSnackBar, showSuccessSnackBar, showInfoSnackBar } from "@/utils/SnackBartHelper";
import debounce from 'lodash/debounce';

export default {
  name: "RightSidebar",
  props: {
    //hide side bar
    isSidebarMini: {
      type: Boolean,
      default: false,
    },
    isOpen: {
      type: Boolean,
      default: true
    },
    // Título da sidebar
    title: {
      type: String,
      default: "Itens"
    },
    // Lista de itens
    items: {
      type: Array,
      default: () => []
    },
    // Mostrar campo de busca
    showSearch: {
      type: Boolean,
      default: true
    },
    // Mostrar botão de adicionar
    showActions: {
      type: Boolean,
      default: false
    },
    // Estado de loading
    loading: {
      type: Boolean,
      default: false
    },
    // Mostrar seção de setores
    showSectors: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    searchText: "",
    filteredItems: [],
    units: [],
    usersByRank: [],
    isLoadingUnits: false,
    sectorsByUnitList: {}, // agora é um objeto: { [unitId]: [setores...] }
    isLoadingSectors: {}, // loading por unidade
    expandedUnitIds: [], // agora é um array para múltiplos setores
    selectedSectorIds: [], // array de ids dos setores selecionados
    selectedUnits: [], // array de ids das unidades selecionadas
    selectedUnitsLenght: 0,
    reservoirUnitIds: null,
    isLoadingReservoirUnits: false,
    reservoirUnitFilterPromise: null,
    // Flag para suprimir lógica do watcher quando revertendo seleção
    suppressEstimedSelection: false,
    // Flag para não fazer commit ao Vuex quando só estamos sincronizando exibição (ex.: centralizar alerta)
    suppressSelectedUnitsCommit: false,
    isReopeningSidebar: false,
    reopenSidebarOpenTimer: null,
    reopenSidebarStopTimer: null,
    reopenSidebarSafetyTimer: null,
    chevronDown,
  }),
  computed: {
    ...mapGetters([
      "isLoggedIn",
      "getSelectedUnit",
      "getSelectedUnits",
      "getSelectedSectors"
    ]),
    selectedPeriod() {
      return this.$store.getters.selectedPeriodValue?.period;
    },
    selectedUnitsFromStore() {
      return this.$store.getters.getSelectedUnits || [];
    },

    isConsumption() {
      return this.$route && ['/consumption', '/consumption-by-tariff'].includes(this.$route.path);
    },
    isReservoir() {
      return this.$route && this.$route.path === '/reservoir';
    },

    isDashboard() {
      return this.$route && this.$route.path === '/dashboard';
    },
    isAlerts() {
      return this.$route && this.$route.path === '/alertas';
    },
    isGoalsForecasts() {
      return this.$route && this.$route.path === '/goals-forecasts';
    },
    isMobile() {
      return this.$vuetify.breakpoint.name == 'xs' || this.$vuetify.breakpoint.name == 'sm'
    },
    drawerWidth() {
      return this.isMobile ? 300 : 336;
    },
    topBarHeight() {
      const width = this.$vuetify.breakpoint.width;

      if (width <= 480) {
        return 48;
      }

      if (width <= 768) {
        return 56;
      }

      return 64;
    },
    drawerStyles() {
      return {
        boxShadow: '-10px 0 32px rgba(15, 23, 42, 0.08)',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #e8eef5',
        position: 'fixed',
        top: `${this.topBarHeight}px`,
        height: `calc(100% - ${this.topBarHeight}px)`,
        zIndex: 100
      };
    },
    reopenButtonStyle() {
      return {
        right: '12px',
        bottom: '20px',
        position: 'fixed',
        zIndex: 200
      };
    },
    showReopenButton() {
      return !this.isOpen;
    },
    sidebarTitle() {
      return this.title;
    },
    selectedItem: {
      get() {
        return this.getSelectedUnit;
      },
      set(value) {
        this.handleUnitSelection(value);
      }
    },
    // Computed property para os itens da sidebar (unidades)
    sidebarItems() {
      return this.units.map(unit => ({
        id: unit.id,
        title: unit.name,
        subtitle: `${unit.name_client} - ${this.getRegistration(unit.matricula)}`,
        image: this.getUnitImage(unit.id_client),
        badge: this.getUnitStatus(unit),
        badgeColor: this.getUnitStatusColor(unit),
        address: this.getUnitAddress(unit.address),
        // Metas e indicadores de consumo
        metaEconomia: unit.metaEconomia,
        averageBeforeFoz: unit.averageBeforeFoz,
        // Volume de economia e meta de consumo calculados a partir da média antes da Foz
        economyVolume:
          unit.averageBeforeFoz != null && unit.metaEconomia != null
            ? unit.averageBeforeFoz * (unit.metaEconomia / 100)
            : null,
        actions: [
          { label: "Visualizar", icon: "mdi-eye" },
          { label: "Editar", icon: "mdi-pencil" }
        ]
      }));
    },
    displaySidebarItems() {
      const items = this.sidebarItems.length > 0 ? this.sidebarItems : this.items;
      if (!this.isReservoir || !Array.isArray(this.reservoirUnitIds)) return items;
      return items.filter(item => this.reservoirUnitIds.includes(item.id));
    },
    visibleFilteredItems() {
      if (!this.isReservoir || !Array.isArray(this.reservoirUnitIds)) return this.filteredItems;
      return this.filteredItems.filter(item => this.reservoirUnitIds.includes(item.id));
    },
    // Retorna true quando o período selecionado é o estimado
    // e existe pelo menos uma unidade selecionada (no Vuex ou localmente)
    isEstimedPeriod() {
      const period = this.$store.getters.selectedPeriodValue && this.$store.getters.selectedPeriodValue.period;
      const selectedFromVuex = this.getSelectedUnits || [];
      const selectedFromLocal = this.selectedUnits || [];
      const hasSelectedUnits =
        (Array.isArray(selectedFromVuex) && selectedFromVuex.length > 0) ||
        (Array.isArray(selectedFromLocal) && selectedFromLocal.length > 0);
      return period === 'estimed' && hasSelectedUnits;
    },
    // Computed para obter os setores completos selecionados
    selectedSectors() {
      // Busca todos os setores de todas as unidades
      const allSectors = Object.values(this.sectorsByUnitList).flat();
      return allSectors.filter(sector => this.selectedSectorIds.includes(sector.id_doc));
    },
    selectedUnitIds() {
      return this.selectedUnits.map(unit => unit.id);
    },
    selectedUnitsCount() {
      return this.selectedUnits.length;
    },
    hasSelection() {
      return this.selectedUnits.length > 0 || this.selectedSectors.length > 0;
    },
    totalUnitsCount() {
      return this.displaySidebarItems.length;
    },
    reopenButtonTooltip() {
      if (!this.hasSelection) {
        return 'Abrir lista de unidades';
      }

      const parts = [];

      if (this.selectedUnitsCount) {
        parts.push(`${this.selectedUnitsCount} unidade(s) selecionada(s)`);
      }

      if (this.selectedSectors.length) {
        parts.push(`${this.selectedSectors.length} setor(es) ativo(s)`);
      }

      return `${parts.join(' • ')}. Clique para abrir a lista.`;
    }
  },
  watch: {
    selectedPeriod(newPeriod, oldPeriod) {
      if (!this.isConsumption) {
        return;
      }
      if (
        newPeriod === 'estimed' &&
        oldPeriod !== 'estimed' &&
        this.selectedUnitsFromStore.length > 1
      ) {
        this.deselectAll();
      }
    },
    items: {
      immediate: true,
      handler() {
        this.filterItems();
      }
    },
    isOpen(newValue) {
      if (newValue) {
        this.clearReopenSafetyTimer();
        this.$nextTick(() => {
          this.stopReopenLoading(250);
        });
      } else {
        this.clearReopenOpenTimer();
        this.clearReopenSafetyTimer();
        this.stopReopenLoading(0);
      }
    },
    sidebarItems: {
      immediate: true,
      handler() {
        this.filterItems();
      }
    },
    isLoggedIn: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.loadUnits();
        } else {
          this.units = [];
          this.filteredItems = [];
          this.reservoirUnitIds = null;
        }
      }
    },
    // Mantém os checkboxes de setores sincronizados quando a seleção vem de outra tela (ex.: Consumo)
    getSelectedSectors: {
      immediate: true,
      handler() {
        this.syncSelectedSectorsFromVuex();
      }
    },
    getSelectedUnits: {
      immediate: true,
      handler(newUnits) {
        const fromVuex = [...(newUnits || [])];
        const currentIds = (this.selectedUnits || []).map(u => u.id).sort();
        const vuexIds = fromVuex.map(u => u.id).sort();
        if (JSON.stringify(currentIds) === JSON.stringify(vuexIds)) return;

        // Mapear unidades do Vuex para itens da sidebar (mesmo formato que :value="item") para o checkbox marcar ao centralizar alerta
        const mapped = fromVuex.map(vu => this.sidebarItems.find(si => si.id === vu.id)).filter(Boolean);
        this.suppressSelectedUnitsCommit = true;
        this.selectedUnits = mapped.length > 0 ? mapped : fromVuex;
      }
    },
    selectedUnits: {
      handler(newUnits, oldUnits) {
        // when user centralize alerta, suppress the selection of units
        if (this.suppressSelectedUnitsCommit || this.suppressEstimedSelection) {
          this.suppressSelectedUnitsCommit = false;
          this.suppressEstimedSelection = false;
          this.selectedUnitsLenght = (newUnits || []).length;
          return;
        }

        // Business rule: when period is 'estimed' and there is already at least one unit selected in Vuex,
        // prevent adding additional units (show snackbar and revert the selection)
        if (!this.isGoalsForecasts && this.$store.getters.selectedPeriodValue?.period === 'estimed' && newUnits.length > 1) {
          showInfoSnackBar("Não é permitido adicionar novas unidades quando o período for 'Estimado'. Selecione apenas uma unidade ou altere o período.");
          // Revert selection to previous state
          this.suppressEstimedSelection = true;
          this.selectedUnits = oldUnits.slice();
          return;
        }

        if (this.isGoalsForecasts && newUnits.length > 1) {
          const lastSelectedUnit = newUnits[newUnits.length - 1];
          showInfoSnackBar("A pagina de metas permite selecionar apenas uma unidade por vez.");
          this.suppressSelectedUnitsCommit = true;
          this.selectedUnits = [lastSelectedUnit];
          this.selectedUnitsLenght = 1;
          this.setSelectedUnit(lastSelectedUnit ? { unit: lastSelectedUnit.unit || lastSelectedUnit, id: lastSelectedUnit.id, ...lastSelectedUnit } : null);
          this.setSelectedUnits(lastSelectedUnit ? [lastSelectedUnit] : []);
          return;
        }

        // if user deselect a unit, deselect the sectors of the unit
        if (oldUnits && newUnits.length < oldUnits.length) {
          const deselectedUnits = oldUnits.filter(oldUnit =>
            !newUnits.some(newUnit => newUnit.id === oldUnit.id)
          );
          deselectedUnits.forEach(deselectedUnit => {
            this.deselectUnitSectors(deselectedUnit.id);
          });
          this.emitSectorsSelected();
        }

        // when all units are deselected, clear the single selected unit in Vuex
        if (!newUnits || newUnits.length === 0) {
          this.setSelectedUnit(null);
        }

        // if user select a new unit, select the sectors of the unit
        if (newUnits.length > this.selectedUnitsLenght) {
          const lastSelectedUnit = newUnits[newUnits.length - 1];
          if (this.isGoalsForecasts) {
            this.setSelectedUnit(lastSelectedUnit ? { unit: lastSelectedUnit.unit || lastSelectedUnit, id: lastSelectedUnit.id, ...lastSelectedUnit } : null);
          } else {
            const hasMatchingSector = this.selectedSectors.some(sector => sector.id_unit === lastSelectedUnit.id);
            if (hasMatchingSector) this.selectItem(lastSelectedUnit);
            this.selectItem(lastSelectedUnit);
          }
        }

        this.selectedUnitsLenght = newUnits.length;
        this.setSelectedUnits([...newUnits]);
        this.$emit('units-selected', {
          unit: newUnits
        });
      }
    },
    // Quando entrar no dashboard ou alertas, garante que nenhum setor fique aberto; na página de alertas apenas unidades são usadas
    '$route.path'(newPath) {
      if (newPath === '/dashboard') {
        this.expandedUnitIds = [];
      }
      if (newPath === '/alertas') {
        this.expandedUnitIds = [];
        this.selectedSectorIds = [];
        this.$store.commit('SET_SELECTED_SECTORS', []);
        const period = this.$store.getters.selectedPeriodValue?.period;
        const vuexUnits = this.getSelectedUnits || [];
        if (period === 'estimed' && Array.isArray(vuexUnits) && vuexUnits.length > 1) {
          this.suppressSelectedUnitsCommit = true;
          this.setSelectedUnits([vuexUnits[0]]);
        }
      }

      if (newPath === '/goals-forecasts') {
        this.expandedUnitIds = [];
        this.selectedSectorIds = [];
        this.$store.commit('SET_SELECTED_SECTORS', []);
        const vuexUnits = this.getSelectedUnits || [];
        if (Array.isArray(vuexUnits) && vuexUnits.length > 1) {
          this.suppressSelectedUnitsCommit = true;
          this.setSelectedUnits([vuexUnits[0]]);
          this.setSelectedUnit(vuexUnits[0]);
        }
      }

      if (newPath === '/reservoir') {
        this.loadReservoirUnitFilter();
        if (this.selectedUnitsFromStore.length > 0 || this.selectedUnits.length > 0 || this.selectedSectors.length > 0) {
          this.deselectAll();
        }
      } else {
        this.filterItems();
      }

      //when user go to consumption from any route, if have more than one unit selected, deselectAll is called
      if (['/consumption', '/consumption-by-tariff'].includes(newPath)) {
        if (this.selectedUnitsFromStore.length > 1) {
          this.deselectAll();
        }
      }
    }
  },
  methods: {
    ...mapActions(['setSelectedUnit', 'setSelectedSectors', 'setSelectedUnits']),

    async loadReservoirUnitFilter() {
      if (!this.isReservoir || Array.isArray(this.reservoirUnitIds)) return;
      if (this.reservoirUnitFilterPromise) return this.reservoirUnitFilterPromise;

      this.isLoadingReservoirUnits = true;
      this.reservoirUnitFilterPromise = SectorsService.getSectors()
        .then((sectors) => {
          this.reservoirUnitIds = [
            ...new Set((sectors || [])
              .filter(sector => sector.waterSupplyType === 'RESERVOIR' && sector.id_unit)
              .map(sector => sector.id_unit))
          ];
        })
        .catch((error) => {
          console.error('Erro ao carregar unidades com reservatório:', error);
          this.reservoirUnitIds = [];
        })
        .finally(() => {
          this.isLoadingReservoirUnits = false;
          this.reservoirUnitFilterPromise = null;
          this.filterItems();
        });

      return this.reservoirUnitFilterPromise;
    },
    handleReopenSidebar() {
      if (this.isOpen || this.reopenSidebarOpenTimer) return;

      this.isReopeningSidebar = true;
      this.clearReopenStopTimer();
      this.clearReopenSafetyTimer();

      this.reopenSidebarOpenTimer = setTimeout(() => {
        this.$emit('update:isOpen', true);
        this.reopenSidebarOpenTimer = null;
      }, 70);

      this.reopenSidebarSafetyTimer = setTimeout(() => {
        if (!this.isOpen) {
          this.isReopeningSidebar = false;
        }
        this.reopenSidebarSafetyTimer = null;
      }, 1500);
    },

    stopReopenLoading(delay = 0) {
      this.clearReopenStopTimer();

      this.reopenSidebarStopTimer = setTimeout(() => {
        this.isReopeningSidebar = false;
        this.reopenSidebarStopTimer = null;
      }, delay);
    },

    clearReopenOpenTimer() {
      if (this.reopenSidebarOpenTimer) {
        clearTimeout(this.reopenSidebarOpenTimer);
        this.reopenSidebarOpenTimer = null;
      }
    },

    clearReopenStopTimer() {
      if (this.reopenSidebarStopTimer) {
        clearTimeout(this.reopenSidebarStopTimer);
        this.reopenSidebarStopTimer = null;
      }
    },

    clearReopenSafetyTimer() {
      if (this.reopenSidebarSafetyTimer) {
        clearTimeout(this.reopenSidebarSafetyTimer);
        this.reopenSidebarSafetyTimer = null;
      }
    },

    syncSelectedSectorsFromVuex() {
      const vuexSectors = this.$store.getters.getSelectedSectors || [];
      this.selectedSectorIds = vuexSectors.map(s => s.id_doc);
      const unitIds = [...new Set(vuexSectors.map(s => s.id_unit))];
      this.expandedUnitIds = unitIds;
    },

    syncSelectedUnitsFromVuex() {
      const vuexUnits = this.$store.getters.getSelectedUnits || [];
      this.selectedUnitsLenght = vuexUnits.length;
      this.selectedUnits = [...vuexUnits];
    },

    async loadUnits() {
      if (this.isLoggedIn && !this.isLoadingUnits) {
        this.isLoadingUnits = true;
        try {
          const usersResponse = await UsersService.findAllClients();
          this.usersByRank = usersResponse.data.data;

          const unitsResponse = await UnitsServiceServer.findAllFranchisee();
          this.units = unitsResponse.data.data;
          if (this.isReservoir) {
            await this.loadReservoirUnitFilter();
          }

          if (!this.getSelectedUnit && this.units.length > 0) {
            const firstUnit = this.isReservoir && Array.isArray(this.reservoirUnitIds)
              ? this.units.find(unit => this.reservoirUnitIds.includes(unit.id))
              : this.units[0];
            if (firstUnit) {
              await this.loadSectorsByUnit(firstUnit.id, true);
              this.setSelectedUnits([{ unit: firstUnit, id: firstUnit.id, ...firstUnit }]);
            }
          }

          const vuexSectors = this.$store.getters.getSelectedSectors || [];
          const unitIdsToLoad = [...new Set(vuexSectors.map(s => s.id_unit))];
          for (const unitId of unitIdsToLoad) {
            await this.loadSectorsByUnit(unitId);
          }
          // this.syncSelectedSectorsFromVuex();
          // this.syncSelectedUnitsFromVuex();
        } catch (error) {
          console.error('Erro ao carregar unidades:', error);
        } finally {
          this.isLoadingUnits = false;
        }
      }
    },

    async loadSectorsByUnit(unitId, selectFirstSector = false) {
      if (!unitId || this.isLoadingSectors[unitId]) return;
      this.$set(this.isLoadingSectors, unitId, true);
      try {
        const sectors = await SectorsService.getSectorsByUnitId(unitId);
        this.$set(this.sectorsByUnitList, unitId, sectors);
        this.$emit('sectors-loaded', {
          unitId: unitId,
          sectors: sectors
        });
        const selectableSectors = this.filteredSectorsByUnit(unitId);
        if (selectFirstSector && selectableSectors && selectableSectors.length > 0) {
          this.selectedSectorIds = [selectableSectors[0].id_doc];
          this.emitSectorsSelected();
        }
      } catch (error) {
        console.error('Erro ao carregar setores:', error);
        this.$set(this.sectorsByUnitList, unitId, []);
        this.$emit('sectors-error', {
          unitId: unitId,
          error: error
        });
      } finally {
        this.$set(this.isLoadingSectors, unitId, false);
      }
    },

    /**
     * Regra de exibição dos setores por tela:
     * - /reservoir: apenas setores RESERVOIR
     * - /consumption: apenas setores que NÃO sejam RESERVOIR
     * - demais telas: todos os setores
     */
    filteredSectorsByUnit(unitId) {
      const sectors = this.sectorsByUnitList[unitId] || [];
      const path = this.$route && this.$route.path ? this.$route.path : '';

      if (path === '/reservoir') {
        return sectors.filter(sector => sector.waterSupplyType === 'RESERVOIR');
      }

      if (['/consumption', '/consumption-by-tariff'].includes(path)) {
        return sectors.filter(sector => sector.waterSupplyType !== 'RESERVOIR');
      }

      return sectors;
    },

    getUnitImage(userId) {
      const user = this.usersByRank.find(({ id }) => userId === id);
      if (user && user.metadata && user.metadata.imageURL) {
        return user.metadata.imageURL;
      }
      return DefaultAddImg;
    },

    getRegistration(registration) {
      return registration == null || registration == "" ? "Sem matrícula" : registration;
    },

    getUnitStatus(unit) {
      // Lógica para determinar o status da unidade
      if (unit.metaEconomia) {
        return `${unit.metaEconomia}%`;
      }
      return "N/A";
    },

    getUnitStatusColor(unit) {
      // Lógica para determinar a cor do status
      if (unit.metaEconomia) {
        if (unit.metaEconomia >= 80) return "success";
        if (unit.metaEconomia >= 50) return "warning";
        return "error";
      }
      return "grey";
    },

    getUnitAddress(address) {
      if (!address) return "Endereço não disponível";

      const parts = [];

      // Logradouro e número
      if (address.logradouro) {
        const logradouro = address.logradouro;
        const numero = address.numero ? `, ${address.numero}` : '';
        parts.push(`${logradouro}${numero}`);
      }

      // Complemento
      if (address.complemento) {
        parts.push(address.complemento);
      }

      // Bairro
      if (address.bairro) {
        parts.push(address.bairro);
      }

      // Cidade e UF
      if (address.cidade) {
        const cidade = address.cidade;
        const uf = address.uf ? ` - ${address.uf}` : '';
        parts.push(`${cidade}${uf}`);
      }

      // CEP
      if (address.cep) {
        parts.push(`CEP: ${address.cep}`);
      }

      return parts.length > 0 ? parts.join(', ') : "Endereço não disponível";
    },

    formatVolume(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return "Não há dados";
      }
      const numeric = Number(value);
      return `${numeric.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} m³`;
    },

    handleUnitSelection(unit) {
      this.setSelectedUnit(unit);
    },
    toggleSidebar() {
      this.$emit('toggle');
    },
    debouncedFilterItems: debounce(function () {
      this.filterItems();
    }, 300),

    filterItems() {
      const itemsToFilter = this.displaySidebarItems;

      const search = this.searchText || '';
      if (!search) {
        this.filteredItems = [...itemsToFilter];
      } else {
        const searchLower = search.toLowerCase();
        this.filteredItems = itemsToFilter.filter(item => {
          const title = item.title || '';
          const subtitle = item.subtitle || '';
          return (
            title.toLowerCase().includes(searchLower) ||
            subtitle.toLowerCase().includes(searchLower)
          );
        });
      }
    },

    openSectors(item) {
      const idx = this.expandedUnitIds.indexOf(item.id);
      if (idx === -1) {
        this.expandedUnitIds.push(item.id);
        this.loadSectorsByUnit(item.id);
      } else {
        this.expandedUnitIds.splice(idx, 1);
      }
    },

    handleRowClick(item) {
      // Bloqueia apenas a interação de seleção na linha
      // quando a exibição de setores não está habilitada
      // ou quando estamos em telas que utilizam apenas unidades.
      if (!this.showSectors || this.isDashboard || this.isAlerts || this.isGoalsForecasts) {
        return;
      }
      this.selectItem(item);
    },

    async selectItem(item) {
      item = { unit: this.units.find(unit => unit.id === item.id), ...item };

      const unitIsSelected = this.selectedUnitIds.includes(item.id);
      if (unitIsSelected) {
        let sectors = this.sectorsByUnitList[item.id] || [];
        if (sectors.length === 0) {
          await this.loadSectorsByUnit(item.id);
          sectors = this.sectorsByUnitList[item.id] || [];
        }
        // Em páginas como /consumption, apenas setores visíveis (não-RESERVOIR) devem influenciar a lógica
        const visibleSectors = this.filteredSectorsByUnit(item.id);
        const hasSelectedSectors = Array.isArray(this.selectedSectorIds) && visibleSectors.some(s => this.selectedSectorIds.includes(s.id_doc));
        if (hasSelectedSectors) {
          showInfoSnackBar('Não é possível deselecionar todos os setores de uma unidade selecionada. Deselecione a unidade primeiro.')
          return;
        }
      }

      this.handleUnitSelection(item);
      this.$emit('select-item', item);
      let sectors = this.sectorsByUnitList[item.id] || [];
      if (sectors.length === 0) {
        await this.loadSectorsByUnit(item.id);
        sectors = this.sectorsByUnitList[item.id] || [];
      }
      // Garante que apenas setores que realmente aparecem na interface sejam considerados
      const visibleSectors = this.filteredSectorsByUnit(item.id);
      const hasSelectedSectors = Array.isArray(this.selectedSectorIds) && visibleSectors.some(s => this.selectedSectorIds.includes(s.id_doc));
      if (hasSelectedSectors) {
        this.selectedSectorIds = this.selectedSectorIds.filter(id => !visibleSectors.some(s => s.id_doc === id));
        this.expandedUnitIds = this.expandedUnitIds.filter(id => id !== item.id);
        this.$nextTick(() => {
          const allSectors = Object.values(this.sectorsByUnitList).flat();
          const stillSelectedSector = allSectors.find(s => this.selectedSectorIds.includes(s.id_doc));
          if (stillSelectedSector) {
            const unit = this.units.find(u => u.id === stillSelectedSector.id_unit);
            if (unit) {
              this.handleUnitSelection({ unit, id: unit.id, ...unit });
            }
          } else {
            this.handleUnitSelection(null);
          }
          this.emitSectorsSelected();
        });
      } else {
        if (!this.expandedUnitIds.includes(item.id)) {
          this.expandedUnitIds.push(item.id);
        }
        visibleSectors.forEach(sector => {
          if (!this.selectedSectorIds.includes(sector.id_doc)) {
            this.selectedSectorIds.push(sector.id_doc);
          }
        });
      }
      this.syncExpandedUnitsWithSelectedSectors();
      this.emitSectorsSelected();
    },

    handleAction(action, item) {
      this.$emit('item-action', { action, item });
    },

    handleAddItem() {
      this.$emit('add-item');
    },

    shouldShowTooltip(item) {
      // Mostrar tooltip se o título for muito longo ou se houver subtítulo longo
      const titleTooLong = item.title && item.title.length > 25;
      const subtitleTooLong = item.subtitle && item.subtitle.length > 35;
      const hasBadge = item.badge;
      const hasAddress = item.address && item.address !== "Endereço não disponível";

      if (hasBadge || hasAddress) return true;

      return titleTooLong || subtitleTooLong;
    },

    getSectorType(sector) {
      // Determinar o tipo do setor baseado no waterSupplyType
      if (sector.waterSupplyType) {
        switch (sector.waterSupplyType) {
          case 'WELL':
            return 'Poço';
          case 'RESERVOIR':
            return 'Reservatório';
          case 'DEALERSHIP':
            return 'Concessionária';
          default:
            return 'N/A';
        }
      }
      return 'N/A';
    },

    getSectorStatusColor(sector) {
      if (sector.invoiced !== undefined) {
        return sector.invoiced ? 'success' : 'warning';
      }
      return 'grey';
    },

    toggleSectorSelection(sector) {
      const unitIsSelected = this.selectedUnitIds.includes(sector.id_unit);

      const wasSelected = this.selectedSectorIds.includes(sector.id_doc);

      if (unitIsSelected && wasSelected) {
        const unitSectors = this.sectorsByUnitList[sector.id_unit] || [];
        const selectedUnitSectors = unitSectors.filter(s =>
          this.selectedSectorIds.includes(s.id_doc) && s.id_doc !== sector.id_doc
        );

        if (selectedUnitSectors.length === 0) {
          this.$nextTick(() => {
            this.selectedSectorIds.push(sector.id_doc);
          });

          showInfoSnackBar('Não é possível deselecionar todos os setores de uma unidade selecionada. Deselecione a unidade primeiro.')
          return;
        }
      }

      const allSectors = Object.values(this.sectorsByUnitList).flat();
      const stillSelectedSector = allSectors.find(s => this.selectedSectorIds.includes(s.id_doc));
      this.$nextTick(() => {
        if (this.selectedSectorIds.includes(sector.id_doc)) {
          const unit = this.units.find(u => u.id === sector.id_unit);
          if (unit) {
            this.handleUnitSelection({ unit, id: unit.id, ...unit });
          }
        } else if (stillSelectedSector) {
          const unit = this.units.find(u => u.id === stillSelectedSector.id_unit);
          if (unit) {
            this.handleUnitSelection({ unit, id: unit.id, ...unit });
          }
        } else {
          this.handleUnitSelection(null);
        }
        this.emitSectorsSelected();
      });
    },

    emitSectorsSelected() {
      this.setSelectedSectors(this.selectedSectors);
      this.$emit('sectors-selected', {
        unitId: this.expandedUnitIds,
        sectors: this.selectedSectors
      });
      
      if (!this.selectedSectors.length) {
        this.setSelectedUnit(null);
      }

      this.syncExpandedUnitsWithSelectedSectors();
    },

    syncExpandedUnitsWithSelectedSectors() {
      const unitIdsWithSelectedSectors = this.selectedSectors.map(sector => sector.id_unit);
      unitIdsWithSelectedSectors.forEach(unitId => {
        if (!this.expandedUnitIds.includes(unitId)) {
          this.expandedUnitIds.push(unitId);
        }
      });
    },

    isSectorDisabled(sector) {
      // Se estamos no período estimado, desabilita todos os checkboxes de setor
      if (this.isEstimedPeriod) return true;

      const unitIsSelected = this.selectedUnitIds.includes(sector.id_unit);

      if (!unitIsSelected) {
        return false;
      }

      const unitSectors = this.sectorsByUnitList[sector.id_unit] || [];
      const selectedUnitSectors = unitSectors.filter(s => this.selectedSectorIds.includes(s.id_doc));

      return selectedUnitSectors.length === 1 && this.selectedSectorIds.includes(sector.id_doc);
    },
    deselectUnitSectors(unitId) {
      // Ao desmarcar uma unidade, só precisamos limpar setores que são exibidos no contexto atual
      const unitSectors = this.filteredSectorsByUnit(unitId);
      unitSectors.forEach(sector => {
        const sectorIndex = this.selectedSectorIds.indexOf(sector.id_doc);
        if (sectorIndex > -1) {
          this.selectedSectorIds.splice(sectorIndex, 1);
        }
      });
      const expandedIndex = this.expandedUnitIds.indexOf(unitId);
      if (expandedIndex > -1) {
        this.expandedUnitIds.splice(expandedIndex, 1);
      }
    },
    deselectAll() {
      this.selectedUnits = [];
      this.selectedSectorIds = [];
      this.expandedUnitIds = [];

      this.setSelectedUnit(null);
      this.setSelectedSectors([]);

      this.$emit('sectors-selected', {
        unitId: [],
        sectors: []
      });
    },
    handleDeselectAll() {
      if (!this.hasSelection) return;
      this.deselectAll();
    },
  },
  mounted() {
    this.loadUnits();
    this.syncSelectedSectorsFromVuex();
    this.syncSelectedUnitsFromVuex();
    if (this.$route?.path === '/goals-forecasts') {
      this.expandedUnitIds = [];
      this.selectedSectorIds = [];
      this.$store.commit('SET_SELECTED_SECTORS', []);
      const vuexUnits = this.getSelectedUnits || [];
      if (Array.isArray(vuexUnits) && vuexUnits.length > 1) {
        this.suppressSelectedUnitsCommit = true;
        this.setSelectedUnits([vuexUnits[0]]);
        this.setSelectedUnit(vuexUnits[0]);
      }
    }
  },
  beforeDestroy() {
    this.clearReopenOpenTimer();
    this.clearReopenStopTimer();
    this.clearReopenSafetyTimer();
  }
};
</script>

<style scoped>
.right-sidebar-drawer {
  overflow: hidden;
}

.right-sidebar__content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.right-sidebar__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 18px 16px;
  border-bottom: 1px solid #e8eef5;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.right-sidebar__header-copy {
  min-width: 0;
}

.right-sidebar__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a8699;
  margin-bottom: 6px;
}

.right-sidebar__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.right-sidebar__title {
  margin: 0;
  color: #0f172a;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 700;
}

.right-sidebar__count-chip {
  font-weight: 700;
}

.header-icon-btn {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
}

.search-container {
  padding: 14px 16px 12px;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.right-sidebar__search-field ::v-deep .v-input__slot {
  border-radius: 14px !important;
  min-height: 44px !important;
  background: #f8fafc;
}

.right-sidebar__search-field ::v-deep fieldset {
  border-color: #e2e8f0;
}

.items-list-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0 0;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.units-list {
  background: transparent !important;
  padding: 0 0 12px;
}

.units-list__group {
  margin-bottom: 2px;
}

.sidebar-item {
  margin: 0 10px;
  padding: 8px 10px;
  min-height: 66px;
  border-radius: 16px;
  border: 1px solid transparent;
  background: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.sidebar-item:hover {
  background: #f8fbff;
  border-color: #dbe7f5;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
  transform: translateY(-1px);
}

.sidebar-item--active {
  background: #eef6ff;
  border-color: #bfd9ff;
  box-shadow: 0 8px 18px rgba(29, 138, 255, 0.1);
}

.sidebar-item__expand {
  flex-shrink: 0;
  margin-right: 4px;
}

.sidebar-item__body {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.sidebar-item__avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: #f3f7fb;
  border: 1px solid #e6edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.sidebar-item__image {
  width: 100%;
  height: 100%;
}

.sidebar-item__content {
  min-width: 0;
  flex: 1;
}

.sidebar-item__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sidebar-item__title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-item__subtitle {
  margin-top: 2px;
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-item__aside {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 6px;
  flex-shrink: 0;
}

.sidebar-item__checkbox {
  margin: 0;
  padding: 0;
}

.sidebar-item__checkbox ::v-deep .v-input__slot {
  margin-bottom: 0;
}

.sidebar-item__checkbox ::v-deep .v-input--selection-controls__input {
  margin-right: 0;
}

.sidebar-skeleton {
  border-radius: 16px;
}

.empty-state {
  padding: 24px 20px;
  text-align: center;
  color: #64748b;
  font-size: 12px;
}

.right-sidebar__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid #e8eef5;
  background: #ffffff;
}

.right-sidebar__footer-meta {
  color: #64748b;
  font-size: 12px;
  white-space: nowrap;
}

.descelect-button {
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #d6e4f5;
  border-radius: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8fbff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}

.descelect-button:hover {
  background: #eef6ff;
  border-color: #93c5fd;
}

.descelect-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-style: dashed;
}

.descelect-button--disabled:hover {
  background: #f8fbff;
  border-color: #d6e4f5;
}

.sidebar-reopen-btn {
  width: 58px;
  min-height: 64px;
  padding: 8px 6px 10px;
  transition: right 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
  border: 1px solid #dbe7f5;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  color: #1d8aff;
  border-radius: 18px;
  box-shadow: 0 12px 24px rgba(29, 138, 255, 0.16);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
}

.sidebar-reopen-btn:hover {
  background: #f8fbff;
  transform: translateY(-1px);
  box-shadow: 0 16px 28px rgba(29, 138, 255, 0.2);
}

.sidebar-reopen-btn--active {
  border-color: #93c5fd;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
  box-shadow: 0 16px 30px rgba(29, 138, 255, 0.24);
}

.sidebar-reopen-btn--loading {
  cursor: progress;
}

.sidebar-reopen-btn__icon-wrap {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-reopen-btn__icon {
  font-size: 22px !important;
}

.sidebar-reopen-btn__badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: #2563eb;
  color: #ffffff;
  font-size: 10px;
  line-height: 18px;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.28);
}

.sidebar-reopen-btn__status-dot {
  position: absolute;
  right: -1px;
  bottom: 1px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #cbd5e1;
}

.sidebar-reopen-btn__status-dot--active {
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14);
}

.sidebar-reopen-btn__caption {
  color: #64748b;
  font-size: 10px;
  line-height: 1;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.custom-arrow {
  display: inline-block;
  vertical-align: middle;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.custom-arrow.flipped {
  transform: rotate(180deg);
}

.v-skeleton-loader {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.sectors-list {
  margin: 6px 12px 0;
  padding: 8px;
  border-radius: 16px;
  background: #f8fbff !important;
  border: 1px solid #e3edf8;
}

.sector-item {
  background: #ffffff !important;
  border-radius: 12px !important;
  margin-bottom: 6px !important;
  border: 1px solid #e6edf5 !important;
  transition: all 0.2s ease !important;
}

.sector-item:hover {
  background: #f8fbff !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06) !important;
}

.sector-item .v-list-item__content {
  padding: 8px 0 !important;
}

.sector-item .v-list-item__title {
  font-size: 12px !important;
  line-height: 1.2 !important;
}

.sector-item__subtitle {
  color: #64748b !important;
}

.sector-item .v-list-item__subtitle {
  font-size: 10px !important;
  line-height: 1.2 !important;
  margin-top: 2px !important;
}

.sector-item .v-checkbox--disabled {
  opacity: 0.6 !important;
}

.sector-item .v-checkbox--disabled .v-input__control {
  cursor: not-allowed !important;
}

.sector-item .v-checkbox--disabled .v-input__slot {
  background-color: #f5f5f5 !important;
}

@media (max-width: 768px) {
  .right-sidebar__header {
    padding: 18px 16px 14px;
  }

  .right-sidebar__title {
    font-size: 18px;
  }

  .sidebar-item {
    margin: 0 10px;
  }

  .right-sidebar__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .right-sidebar__footer-meta {
    white-space: normal;
  }
}
</style>

<style>
/* Estilos globais para o tooltip customizado */
.custom-tooltip {
  background: rgba(33, 33, 33, 0.95) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  padding: 0 !important;
  max-width: 280px !important;
  font-family: 'Roboto', sans-serif !important;
}

.custom-tooltip .tooltip-content {
  padding: 16px !important;
  color: white !important;
}

.custom-tooltip .tooltip-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-tooltip .tooltip-title {
  font-weight: 600;
  font-size: 14px;
  color: white;
  line-height: 1.2;
}

.custom-tooltip .tooltip-section {
  margin-bottom: 10px;
}

.custom-tooltip .tooltip-section:last-child {
  margin-bottom: 0;
}

.custom-tooltip .tooltip-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-tooltip .tooltip-value {
  font-size: 13px;
  color: white;
  line-height: 1.3;
  word-break: break-word;
}

.custom-tooltip .tooltip-chip {
  font-size: 11px !important;
  font-weight: 600 !important;
  height: 20px !important;
  min-width: auto !important;
}

.custom-tooltip .tooltip-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-tooltip .tooltip-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.custom-tooltip .tooltip-action-btn {
  font-size: 11px !important;
  height: 24px !important;
  min-width: auto !important;
  padding: 0 8px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease !important;
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}

.custom-tooltip .tooltip-action-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: translateY(-1px) !important;
}

/* Animação de entrada do tooltip */
.custom-tooltip {
  animation: tooltipFadeIn 0.2s ease-out !important;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Melhorias para dispositivos móveis */
@media (max-width: 768px) {
  .custom-tooltip {
    max-width: 240px !important;
  }

  .custom-tooltip .tooltip-content {
    padding: 12px !important;
  }

  .custom-tooltip .tooltip-title {
    font-size: 13px;
  }

  .custom-tooltip .tooltip-value {
    font-size: 12px;
  }
}
</style>
